import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress } from '@mui/material';
import { WalteroCard, WalteroTypography } from '../waltero';
import { useSharedPerformanceCache } from '../../context/SharedPerformanceCache';

/**
 * Simple RSSI Table - Signal strength measurements
 * 
 * Shows daily signal strength readings for all devices in the organization.
 */
const SimpleRSSITable = ({ selectedOrgId, startDate, endDate }) => {
  const [rssiData, setRssiData] = useState({});
  
  const { 
    loadAllPerformanceData,
    getEndpointData,
    getDevices,
    cacheInfo,
    isEndpointLoading
  } = useSharedPerformanceCache();

  useEffect(() => {
    loadRSSIData();
  }, [selectedOrgId, startDate, endDate]);

  // Update data whenever cache changes
  useEffect(() => {
    const devices = getDevices();
    const dataMap = {};
    
    devices.forEach(device => {
      const data = getEndpointData('rssi-daily', device.id);
      if (data) {
        dataMap[device.id] = data;
      }
    });
    
    setRssiData(dataMap);
  }, [cacheInfo.endpointsLoaded, getEndpointData, getDevices]);

  const loadRSSIData = () => {
    if (!selectedOrgId || !startDate || !endDate) return;
    
    loadAllPerformanceData(selectedOrgId, startDate, endDate);
  };

  // Get signal quality color
  const getSignalQualityColor = (rssi) => {
    const absRssi = Math.abs(rssi);
    if (absRssi >= 20) return '#4caf50'; // excellent - green
    if (absRssi >= 15) return '#8bc34a'; // good - light green  
    if (absRssi >= 10) return '#ff9800'; // fair - orange
    return '#f44336'; // poor - red
  };

  // Get devices from shared cache
  const devices = getDevices();

  // Generate all dates in the selected range (regardless of data availability)
  const generateDateRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);
    
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  // Generate dates in reverse chronological order (most recent first)
  const sortedDates = startDate && endDate ? generateDateRange(startDate, endDate).sort((a, b) => b.localeCompare(a)) : [];


  return (
    <WalteroCard sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <WalteroTypography variant="h6">
          Device RSSI Signal Strength - Daily Averages
        </WalteroTypography>
        {cacheInfo.isLoading && (
          <CircularProgress size={16} />
        )}
      </Box>
      
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
Signal strength measurements in dBm. Color coded by signal quality: Green (excellent), Orange (fair), Red (poor).
      </Typography>
      

      {devices.length === 0 ? (
        <Typography>No devices found for this organization.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3e5f5' }}>
                  Device
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3e5f5' }}>
                  Meter
                </TableCell>
                {sortedDates.map(date => (
                  <TableCell 
                    key={date} 
                    align="center" 
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#f3e5f5',
                      minWidth: '80px',
                      fontSize: '12px'
                    }}
                  >
                    {date.split('-').slice(1).join('/')}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f3e5f5' }}>
                  Overall Avg
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.sort((a, b) => {
                const serialA = String(a.serialnumber || '');
                const serialB = String(b.serialnumber || '');
                return serialA.localeCompare(serialB);
              }).map(device => {
                const deviceRSSI = rssiData[device.id];
                const overallAvg = deviceRSSI?.summary?.overallAverage || 0;
                const signalQuality = deviceRSSI?.summary?.signalQuality || 'none';
                
                return (
                  <TableRow key={device.id}>
                    <TableCell sx={{ fontWeight: '600', fontSize: '12px' }}>
                      {device.serialnumber}
                    </TableCell>
                    <TableCell sx={{ fontSize: '10px', color: '#666' }}>
                      {device.externalmeterid || 'N/A'}
                    </TableCell>
                    {sortedDates.map(date => {
                      const dayData = deviceRSSI?.dailyRSSI?.find(day => day.date === date);
                      const rssi = dayData?.averageRSSI;
                      
                      return (
                        <TableCell 
                          key={date} 
                          align="center"
                          sx={{
                            backgroundColor: rssi !== undefined ? '#fafafa' : '#fafafa',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: rssi !== undefined ? getSignalQualityColor(rssi) : '#999'
                          }}
                        >
                          {isEndpointLoading('rssi-daily', device.id) ? (
                            <CircularProgress size={12} />
                          ) : rssi !== undefined ? (
                            `${rssi} dBm`
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: '600', 
                        backgroundColor: '#f3e5f5',
                        color: overallAvg !== 0 ? getSignalQualityColor(overallAvg) : '#999'
                      }}
                    >
                      {overallAvg !== 0 ? (
                        <div>
                          <div>{overallAvg} dBm</div>
                          <div style={{ fontSize: '9px', textTransform: 'capitalize' }}>
                            {signalQuality}
                          </div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Summary */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f3e5f5', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: '600' }}>
          Summary: {devices.length} devices, {Object.keys(rssiData).length} with RSSI data, {sortedDates.length} days
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Signal Quality: Green (excellent ≥20), Light Green (good ≥15), Orange (fair ≥10), Red (poor &lt;10)
        </Typography>
      </Box>
    </WalteroCard>
  );
};

export default SimpleRSSITable;